// src/WebServer/OncoRisk/content/modules/analysistools/OncoRiskNetworkGraph.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { forceCollide, forceManyBody } from 'd3-force'; // Import D3 forces
import ForceGraph2D from 'react-force-graph-2d';
import { getItemColor } from '../../../config/oncorisk-config'; // Corrected path
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons'; // Import all solid icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './OncoRiskNetworkGraph.css';
import ReactDOM from 'react-dom';

// Register all FontAwesome solid icons
library.add(fas);

// Function to get Font Awesome icon name based on item type
function getItemIcon(itemType) {
  const defaultIcon = 'circle';
  if (!itemType) return defaultIcon;

  let normalized = itemType.toLowerCase();

  // handle "ies" → "y" (e.g. therapies → therapy), then other trailing "s"
  if (normalized.endsWith('ies')) {
    normalized = normalized.slice(0, -3) + 'y';
  } else if (normalized.endsWith('s')) {
    normalized = normalized.slice(0, -1);
  }

  // Map of item types to Font Awesome icon names
  const iconMap = {
    'feature': 'puzzle-piece',
    'disease': 'heartbeat', 
    'therapy': 'pills',
    'assertion': 'check-circle',
    'evidence': 'file-alt',
    'variant': 'random',
    'molecularprofile': 'dna',
    'phenotype': 'user-tag',
    'source': 'database'
  };

  return iconMap[normalized] || defaultIcon;
}

const OncoRiskNetworkGraph = ({ data, onNodeClick }) => {
  const fgRef = useRef();
  const containerRef = useRef();
  const iconCanvasesRef = useRef({});
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  


  const processData = useCallback((inputData) => {
    if (!inputData || !inputData.length) {
      setGraphData({ nodes: [], links: [] });
      return;
    }

    const nodes = [];
    const links = [];
    const nodeMap = {};
    const connectionCounts = {}; // Track number of connections per node

    // Function to add a node if it doesn't exist yet
    const addNode = (id, name, type) => {
      if (!nodeMap[id]) {
        // Normalize type for color mapping - handle special cases like 'therapies' -> 'therapy' properly
        let colorType;
        if (type.endsWith('ies')) {
          colorType = type.slice(0, -3) + 'y'; // 'therapies' -> 'therapy'
        } else if (type.endsWith('s')) {
          colorType = type.slice(0, -1); // 'variants' -> 'variant'
        } else {
          colorType = type;
        }
        
        const node = {
          id,
          name: name || id,
          type,
          color: getItemColor(colorType),
          icon: getItemIcon(colorType), // Add icon to the node
          size: type === 'features' ? 22 : 8, // Features are significantly larger
          originalType: type, // Store original type for later reference
          isMainNode: type === 'features' // Flag to identify main nodes
        };
        nodes.push(node);
        nodeMap[id] = node;
        connectionCounts[id] = 0; // Initialize connection count
      }
      return nodeMap[id];
    };

    inputData.forEach(feature => {
      // Process the links from features to their associations
      const connectionsToProcess = [
        { key: 'associated_diseases', type: 'diseases' },
        { key: 'associated_therapies', type: 'therapies' },
        { key: 'associated_assertions', type: 'assertions' },
        
        { key: '_molecular_profiles', type: 'molecularprofiles' },
        { key: 'variants', type: 'variants' }
      ];

      connectionsToProcess.forEach(conn => {
        const associations = feature[conn.key];
        if (!associations || !Array.isArray(associations)) return;

        associations.forEach(item => {
          const source = addNode(feature.id, feature.name, feature.type);
          const target = addNode(item.id, item.name, conn.type);
          
          // Increment connection counts for both source and target
          connectionCounts[source.id]++;
          connectionCounts[target.id]++;
          
          links.push({
            source: source.id,
            target: target.id,
            id: `${source.id}-${target.id}`
          });
        });
      });
    });

    // Adjust node sizes based on connection counts, except for 'features' types
    nodes.forEach(node => {
      if (node.originalType !== 'features') { // Don't resize feature nodes
        // Base size is 8, increase more dramatically for connections
        const connectionCount = connectionCounts[node.id];
        if (connectionCount > 1) {
          // More dramatic increase: add 3 for each connection beyond the first
          // Min size is 8, max size is 20 (for 5+ connections)
          const additionalSize = Math.min((connectionCount - 1) * 3, 12); // Cap at +12 (for 5+ connections)
          node.size = 8 + additionalSize;
        }
      }
    });

    setGraphData({ nodes, links });
  }, []);

  useEffect(() => {
    processData(data);
  }, [data, processData]);

  useEffect(() => {
    const observerTarget = containerRef.current; // Capture the target element

    const resizeObserverCallback = (entries) => {
      if (!entries || !entries.length) {
        return;
      }
      const entry = entries[0];
      // Use contentRect for more accurate dimensions from ResizeObserver
      const { width, height } = entry.contentRect;
      setDimensions({ width, height });
    };

    const resizeObserver = new ResizeObserver(resizeObserverCallback);

    if (observerTarget) {
      // Observe the target. ResizeObserver will fire initially if element has size.
      resizeObserver.observe(observerTarget);
    }

    // Fallback for general window resize, updates dimensions based on current offsetWidth/Height
    const handleWindowResize = () => {
      if (observerTarget) {
        setDimensions({
          width: observerTarget.offsetWidth,
          height: observerTarget.offsetHeight,
        });
      }
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      if (observerTarget) {
        resizeObserver.unobserve(observerTarget); // Use captured target for unobserve
      }
      window.removeEventListener('resize', handleWindowResize);
      resizeObserver.disconnect(); // Disconnect the observer entirely
    };
  }, [data]); // Dependency on 'data' ensures this effect re-runs if data changes (e.g., placeholder to graph)

  useEffect(() => {
    // Apply D3 forces for better layout
    if (fgRef.current) {
      const chargeForce = forceManyBody().strength(-100); // More repulsion
      fgRef.current.d3Force('charge', chargeForce);
      // Collision detection: radius based on node size, plus a small buffer
      const collideForce = forceCollide(node => node.size + 5).strength(1);
      fgRef.current.d3Force('collide', collideForce);
      // fgRef.current.d3Force('link').distance(50); // Optional: adjust link distance
    }

    // A small delay can help ensure the engine is ready before zooming
    // Also ensure dimensions are valid before trying to zoom
    if (fgRef.current && graphData.nodes.length > 0 && dimensions.width > 0 && dimensions.height > 0) {
        setTimeout(() => {
            fgRef.current.zoomToFit(400, 100);
        }, 150);
    }
  }, [graphData, dimensions]); // Added dimensions to dependency array


  if (!data || data.length === 0) {
    return <div className="ocrp-network-placeholder"></div>;
  }
  
  // No changes needed to the returned JSX
  return (
    <div ref={containerRef} className="ocrp-network-container ocrp-network-grid">
      {dimensions.width > 0 && dimensions.height > 0 && (
        <ForceGraph2D
        ref={fgRef}
        width={dimensions.width}
        height={dimensions.height}
        graphData={graphData}
        nodeLabel="name"
        nodeVal="size"
        nodeCanvasObjectMode={(node) => node.__hover ? 'after' : 'after'}

        nodeCanvasObject={(node, ctx, globalScale) => {
          // Add highlight effect for hovered nodes
          if (node.__hover) {
            ctx.beginPath();
            ctx.arc(node.x, node.y, node.size + 3, 0, 2 * Math.PI, false);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
            ctx.fill();
          }
          
          // Draw the circle for the node
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size + 1, 0, 2 * Math.PI, false);
          ctx.fillStyle = node.color;
          ctx.fill();
          
          ctx.fillStyle = 'white';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Draw icon above the node for all node types
          let symbol;
          switch (node.icon) {
            case 'heartbeat': symbol = '❤'; break;  
            case 'pills': symbol = '⊕'; break;        
            case 'check-circle': symbol = '✓'; break;  
            case 'file-alt': symbol = '⧠'; break;     
            case 'random': symbol = '⋏'; break;        // Variant
            case 'dna': symbol = '⟠'; break;           // Molecular Profile
            case 'user-tag': symbol = '⊗'; break;      // Phenotype
            case 'database': symbol = '◉'; break;      // Source
            default: symbol = ' '; break;
          }
          
          // Position the icon at the top of the circle but inside it
          const iconSize = Math.min(node.size * 0.45, 10);
          ctx.font = `${iconSize}px Arial`;
          // Position it at approximately 2/3 of the way from center to top edge
          ctx.fillText(symbol, node.x, node.y - node.size * 0.65);
          
          if (node.isMainNode) {
            // Features: Show full label (truncated if needed) in center
            const displayName = node.name && node.name.length > 6 ? 
              node.name.substring(0, 6) + '...' : 
              node.name;
              
            const fontSize = Math.min(node.size * 0.3, 14);
            ctx.font = `bold ${fontSize}px Arial`;
            ctx.fillText(displayName, node.x, node.y);
          } 
          else {
            // Non-features: Show shorthand in center, full name below
            // Short label (first 1-2 chars)
            let shortLabel;
            if (node.name && node.name.length > 0) {
              shortLabel = node.name.substring(0, Math.min(2, node.name.length));
            } else {
              shortLabel = '?';
            }
            
            // Display shorthand in center
            const fontSize = Math.min(node.size * 0.7, 14);
            ctx.font = `bold ${fontSize}px Arial`;
            ctx.fillText(shortLabel.toUpperCase(), node.x, node.y);
            
            const fontSize1 = node.name.length < 12
            ? node.size * 0.35
            : Math.min(node.size * 0.25, 11);
          
          ctx.font = `bold ${fontSize1}px Arial`;
          ctx.fillText(
            node.name.length > 12 ? node.name.slice(0, 12) + '..' : node.name,
            node.x,
            node.y + Math.min(node.size * 0.5, 8)
          );
          
          }

        }}
        linkDirectionalParticles={1}
        linkDirectionalParticleWidth={2}
        linkWidth={1}
        linkColor={() => 'rgba(255,255,255,0.2)'}
        onNodeClick={onNodeClick}
        cooldownTicks={100}
        onEngineStop={() => {
          if (fgRef.current && dimensions.width > 0 && dimensions.height > 0) {
            fgRef.current.zoomToFit(400, 100);
          }
        }}
      />
      )}
    </div>
  );
};

export default OncoRiskNetworkGraph;